/**
 * Список рассылок: name, status, recipients/sent/blocked/error.
 * Создать новую → /broadcasts/create.
 *
 * Пагинация на сервере через useTable; auto-refresh каждые 10с через
 * refetchInterval (нужно чтобы статус «running» обновлялся без F5).
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { List, useTable } from "@refinedev/antd";
import { useInvalidate } from "@refinedev/core";
import {
  Table, Tag, Button, Space, Typography, Progress, Popconfirm, message,
} from "antd";
import {
  PlusOutlined, PlayCircleOutlined, StopOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { Link } from "react-router";
import { API_URL } from "../../dataProvider";
import { fmtDateTime } from "../../utils";

interface Broadcast {
  id: number;
  name: string;
  status: string;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  recipients_count: number;
  sent_count: number;
  blocked_count: number;
  error_count: number;
  created_at: string | null;
}

const STATUS_COLOR: Record<string, string> = {
  draft: "default", queued: "blue", running: "processing",
  completed: "green", cancelled: "default", failed: "red",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "Черновик", queued: "В очереди", running: "Идёт",
  completed: "Готово", cancelled: "Отменена", failed: "Ошибка",
};

export const BroadcastList = () => {
  const nav = useNavigate();
  const invalidate = useInvalidate();
  const [acting, setActing] = useState<Record<number, boolean>>({});

  // Серверная пагинация. refetchInterval=10s — auto-poll, чтобы progress
  // у «running» обновлялся без F5.
  const { tableProps } = useTable<Broadcast>({
    resource: "broadcasts",
    syncWithLocation: true,
    pagination: { mode: "server", pageSize: 25 },
    sorters: { initial: [{ field: "id", order: "desc" }] },
    queryOptions: { refetchInterval: 10_000 },
  });

  const refresh = () => invalidate({ resource: "broadcasts", invalidates: ["list"] });

  const act = async (id: number, path: string, okMsg: string) => {
    setActing(s => ({ ...s, [id]: true }));
    try {
      const r = await fetch(`${API_URL}/admin/broadcasts/${id}/${path}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: "{}",
      });
      if (r.ok) { message.success(okMsg); refresh(); }
      else { const t = await r.text(); message.error(t.slice(0, 200)); }
    } finally {
      setActing(s => ({ ...s, [id]: false }));
    }
  };

  const del = async (id: number) => {
    const r = await fetch(`${API_URL}/admin/broadcasts/${id}`, {
      method: "DELETE", credentials: "include",
    });
    if (r.ok) { message.success("Удалено"); refresh(); }
    else { const t = await r.text(); message.error(t.slice(0, 200)); }
  };

  return (
    <List title="Рассылки" breadcrumb={false}
          headerButtons={() => (
            <Button type="primary" icon={<PlusOutlined />}
                    onClick={() => nav("/broadcasts/create")}>
              Новая рассылка
            </Button>
          )}>
      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="name" title="Название"
          render={(v: string, r: Broadcast) => (
            <Link to={`/broadcasts/${r.id}`}>{v}</Link>
          )} />
        <Table.Column dataIndex="status" title="Статус"
          render={(v: string) =>
            <Tag color={STATUS_COLOR[v] || "default"}>{STATUS_LABEL[v] || v}</Tag>} />
        <Table.Column<Broadcast> title="Прогресс"
          render={(_, r) => {
            const total = r.recipients_count || 0;
            const done = r.sent_count + r.blocked_count + r.error_count;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Space direction="vertical" size={0} style={{ minWidth: 200 }}>
                <Progress percent={pct} size="small"
                  status={pct >= 100 ? "success" : "active"} />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {done}/{total} · ✓ {r.sent_count} · 🚫 {r.blocked_count} · ⚠ {r.error_count}
                </Typography.Text>
              </Space>
            );
          }} />
        <Table.Column dataIndex="created_at" title="Создана"
          sorter render={fmtDateTime} />
        <Table.Column<Broadcast> title="" width={160} fixed="right"
          render={(_, r) => (
            <Space size={4}>
              {r.status === "draft" && (
                <Button size="small" icon={<PlayCircleOutlined />}
                  loading={acting[r.id]}
                  onClick={async () => {
                    await act(r.id, "compile", "Аудитория собрана");
                    await act(r.id, "start", "Запущена");
                  }}>
                  Старт
                </Button>
              )}
              {["queued", "running"].includes(r.status) && (
                <Button size="small" danger icon={<StopOutlined />}
                  loading={acting[r.id]}
                  onClick={() => act(r.id, "cancel", "Отменена")}>
                  Стоп
                </Button>
              )}
              {["draft", "cancelled"].includes(r.status) && (
                <Popconfirm title="Удалить?" onConfirm={() => del(r.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </Space>
          )} />
      </Table>
    </List>
  );
};
